import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  KeyboardArrowRight as ArrowRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { productCategoriesApi } from '../services/api';
import CategoryForm from './CategoryForm';

const ProductCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await productCategoriesApi.getAllCategories();
      setCategories(data);
      
      // Calculate stats
      const total = data.length;
      const active = data.filter(cat => cat.active).length;
      const inactive = total - active;
      setStats({ total, active, inactive });
      
      setError(null);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Organize categories into hierarchical structure
  const organizeCategories = (cats) => {
    const rootCategories = cats.filter(cat => !cat.parent_category_id);
    const childCategories = cats.filter(cat => cat.parent_category_id);
    
    const addChildren = (category) => {
      const children = childCategories.filter(child => child.parent_category_id === category.id);
      return {
        ...category,
        children: children.map(addChildren)
      };
    };
    
    return rootCategories.map(addChildren);
  };

  const hierarchicalCategories = organizeCategories(filteredCategories);

  // Toggle category expansion
  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Handle category creation
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setFormDialogOpen(true);
  };

  // Handle category editing
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setFormDialogOpen(true);
  };

  // Handle category deletion
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await productCategoriesApi.deleteCategory(categoryToDelete.id);
      setSuccess(`Category "${categoryToDelete.name}" deleted successfully`);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      setError('Failed to delete category. Make sure it has no subcategories or products.');
    } finally {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  // Handle category deactivation/activation
  const handleToggleActive = async (category) => {
    try {
      if (category.active) {
        await productCategoriesApi.deactivateCategory(category.id);
        setSuccess(`Category "${category.name}" deactivated successfully`);
      } else {
        // For activation, we need to update the category with active: true
        await productCategoriesApi.updateCategory(category.id, {
          name: category.name,
          description: category.description,
          active: true,
          parent_category_id: category.parent_category_id,
          image_url: category.image_url
        });
        setSuccess(`Category "${category.name}" activated successfully`);
      }
      fetchCategories();
    } catch (error) {
      console.error('Error toggling category status:', error);
      setError('Failed to update category status');
    }
  };

  // Handle form submission
  const handleFormSubmit = () => {
    setFormDialogOpen(false);
    setEditingCategory(null);
    fetchCategories();
  };

  // Render category row
  const renderCategoryRow = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);

    return (
      <React.Fragment key={category.id}>
        <TableRow>
          <TableCell>
            {category.image_url ? (
              <img
                src={category.image_url}
                alt={category.name}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #ddd'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #ddd'
                }}
              >
                <CategoryIcon sx={{ color: 'grey.400', fontSize: 20 }} />
              </Box>
            )}
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: level * 3 }}>
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={() => toggleExpanded(category.id)}
                  sx={{ mr: 1 }}
                >
                  {isExpanded ? <ArrowDownIcon /> : <ArrowRightIcon />}
                </IconButton>
              )}
              {!hasChildren && <Box sx={{ width: 32, height: 32, mr: 1 }} />}
              {hasChildren ? (
                isExpanded ? <FolderOpenIcon sx={{ mr: 1, color: 'primary.main' }} /> : <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
              ) : (
                <CategoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
              )}
              <Typography variant="body2" fontWeight={hasChildren ? 'bold' : 'normal'}>
                {category.name}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {category.description || 'No description'}
            </Typography>
          </TableCell>
          <TableCell>
            {category.parent_category_name && (
              <Chip
                label={category.parent_category_name}
                size="small"
                variant="outlined"
              />
            )}
          </TableCell>
          <TableCell>
            <Chip
              label={category.active ? 'Active' : 'Inactive'}
              size="small"
              color={category.active ? 'success' : 'default'}
            />
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {category.translations && Object.keys(category.translations).length > 0 ? (
                <>
                  <LanguageIcon color="primary" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" color="primary">
                    {Object.keys(category.translations).length} language{Object.keys(category.translations).length !== 1 ? 's' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {Object.keys(category.translations).slice(0, 3).map(locale => (
                      <Chip
                        key={locale}
                        label={locale.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 'auto', height: 20, fontSize: '0.7rem' }}
                      />
                    ))}
                    {Object.keys(category.translations).length > 3 && (
                      <Chip
                        label={`+${Object.keys(category.translations).length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 'auto', height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No translations
                </Typography>
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2">
              {category.product_count || 0} products
            </Typography>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Edit Category">
                <IconButton
                  size="small"
                  onClick={() => handleEditCategory(category)}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={category.active ? 'Deactivate' : 'Activate'}>
                <IconButton
                  size="small"
                  onClick={() => handleToggleActive(category)}
                  color={category.active ? 'warning' : 'success'}
                >
                  {category.active ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Category">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(category)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
        {hasChildren && isExpanded && category.children.map(child => renderCategoryRow(child, level + 1))}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading categories...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Product Categories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateCategory}
        >
          Add Category
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Categories
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Categories
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Inactive Categories
              </Typography>
              <Typography variant="h4" color="text.secondary">
                {stats.inactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Categories Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Parent Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Translations</TableCell>
              <TableCell>Products</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hierarchicalCategories.map(category => renderCategoryRow(category))}
            {filteredCategories.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'No categories match your search' : 'No categories found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Category Form Dialog */}
      <CategoryForm
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={handleFormSubmit}
        editingCategory={editingCategory}
        categories={categories}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category "{categoryToDelete?.name}"?
            This action cannot be undone and will fail if the category has subcategories or products.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductCategories;